'use client';

import { useSession } from '@/hooks/use-session';
import { type Icon, IconBell, IconCirclePlusFilled } from '@tabler/icons-react';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar';
import { useCreateAsset } from '@/hooks/use-create-asset';
import { cn } from '@/lib/utils';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { CreateAssetWizard } from './assets/create-asset-wizard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

export function NavMain({
  items
}: {
  items: {
    title: string;
    url: string;
    icon?: Icon;
  }[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { session } = useSession();
  const isMemecoins = session?.role === 'memecoins';
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const isActive = (url: string) => pathname === url || (url !== '/' && pathname.startsWith(url));

  const dropdownItems = [
    { value: 'assets', label: 'Assets' },
    { value: 'memecoins', label: 'Memecoin' },
    { value: 'news', label: 'News' },
    { value: 'analysis', label: 'Analysis' }
  ];

  const availableDropdownItems = isMemecoins
    ? dropdownItems.filter((item) => item.value === 'memecoins')
    : dropdownItems;

  const createAssetMutation = useCreateAsset();

  const handleCreateAsset = async (formData: any) => {
    try {
      await createAssetMutation.mutateAsync(formData);
      setIsWizardOpen(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError
    }
  };

  function handleCreateItem(item: 'assets' | 'memecoins' | 'news' | 'analysis') {
    switch (item) {
      case 'news': {
        router.push('/create/news');
        break;
      }
      case 'analysis': {
        router.push('/create/trade');
        break;
      }
      default: {
        setIsWizardOpen(true);
        break;
      }
    }
  }

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-3">
        <SidebarMenu>
          <SidebarMenuItem className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  tooltip="Quick Create"
                  className="min-w-8 cursor-pointer rounded-[10px] bg-primary text-primary-foreground duration-200 ease-out hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground"
                >
                  <IconCirclePlusFilled />
                  <span>Quick Create</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="border-white/[0.08] bg-[#0D1117]">
                {availableDropdownItems.map((item) => (
                  <DropdownMenuItem
                    key={item.value}
                    onClick={() => handleCreateItem(item.value as any)}
                    className="cursor-pointer text-white/70 hover:text-white focus:text-white"
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {!isMemecoins && (
              <Button
                size="icon"
                className="size-8 border-white/[0.08] text-white/40 hover:text-white/80 group-data-[collapsible=icon]:opacity-0"
                variant="outline"
              >
                <IconBell />
                <span className="sr-only">Dispatch Notification</span>
              </Button>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          {items.map((item) => {
            const active = isActive(item.url);
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton tooltip={item.title} asChild isActive={active}>
                  <Link
                    href={item.url}
                    className={cn(
                      'cursor-pointer rounded-[10px] transition-all duration-150',
                      active
                        ? 'bg-white/[0.08] font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.15)]'
                        : 'text-white/50 hover:bg-white/[0.04] hover:text-white/80'
                    )}
                  >
                    {item.icon && (
                      <item.icon
                        className={cn('size-[17px]', active ? 'text-white/90' : 'text-white/35')}
                      />
                    )}
                    <span className="text-[13px] tracking-[-0.01em]">{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>

      <Sheet open={isWizardOpen} onOpenChange={setIsWizardOpen}>
        <SheetContent className="w-full overflow-y-auto border-white/[0.06] bg-[#0D1117] p-8 sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="text-white">Create New Asset</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <CreateAssetWizard
              onSubmit={handleCreateAsset}
              isLoading={createAssetMutation.isPending}
            />
          </div>
        </SheetContent>
      </Sheet>
    </SidebarGroup>
  );
}
